/**
 * BookCallback — the PUBLIC patient-facing booking page at /book/:token.
 *
 * Texted to leads as a "pick a time for a quick call" link. No auth, no CRM
 * shell, no platform contexts — the token (an unguessable per-contact uuid
 * with a 7-day expiry) is the only credential, and the API returns first name
 * + open slots only. Uses plain fetch (not the axios client) so no auth
 * headers, session refresh, or localStorage context ever touch this page.
 *
 * States: loading -> picker (day tabs + slot grid) -> confirming -> booked.
 * A slot lost to a race (409 slot_taken) refreshes the grid with a notice.
 * Mobile-first: most patients open this from the SMS on their phone.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Slot {
  iso: string;
  label: string;
}
interface Day {
  date: string;
  label: string;
  slots: Slot[];
}
interface BookingState {
  first_name?: string | null;
  booked?: { scheduled_at: string; label: string } | null;
  days?: Day[];
  timezone?: string;
}

type Phase = 'loading' | 'invalid' | 'expired' | 'pick' | 'booked' | 'error';

export default function BookCallback() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [data, setData] = useState<BookingState>({});
  const [dayIdx, setDayIdx] = useState(0);
  const [submitting, setSubmitting] = useState<string | null>(null); // slot iso in flight
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setPhase('loading');
    try {
      const res = await fetch(`${API_URL}/api/crm/public/booking/${token}`);
      if (res.status === 404) {
        const body = await res.json().catch(() => ({}));
        setPhase(body?.error === 'link_expired' ? 'expired' : 'invalid');
        return;
      }
      if (!res.ok) {
        setPhase('error');
        return;
      }
      const body: BookingState = await res.json();
      setData(body);
      setDayIdx(0);
      setPhase(body.booked ? 'booked' : 'pick');
    } catch {
      setPhase('error');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const book = async (slot: Slot) => {
    if (submitting) return;
    setSubmitting(slot.iso);
    setNotice(null);
    try {
      const res = await fetch(`${API_URL}/api/crm/public/booking/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot: slot.iso }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        setData((prev) => ({ ...prev, booked: { scheduled_at: body.scheduled_at, label: body.label } }));
        setPhase('booked');
        return;
      }
      if (res.status === 409 && body?.error === 'already_booked' && body?.booked) {
        setData((prev) => ({ ...prev, booked: body.booked }));
        setPhase('booked');
        return;
      }
      if (res.status === 409) {
        setNotice('Sorry, that time was just taken — here are the latest openings.');
        await load();
        return;
      }
      setNotice('Something went wrong booking that time. Please try another slot.');
    } catch {
      setNotice('Connection problem — please check your signal and try again.');
    } finally {
      setSubmitting(null);
    }
  };

  const greeting = data.first_name ? `Hi ${data.first_name}!` : 'Hi there!';
  const day = data.days?.[dayIdx];

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 dark:bg-gray-900">
      <div className="mx-auto w-full max-w-md">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-600 text-xl font-bold text-white">W</div>
          <h1 className="mt-3 text-lg font-semibold text-gray-900 dark:text-gray-100">West Clinical Research</h1>
        </div>

        <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          {phase === 'loading' && (
            <div className="flex items-center justify-center py-12">
              <div className="h-7 w-7 animate-spin rounded-full border-b-2 border-primary-600" />
            </div>
          )}

          {(phase === 'invalid' || phase === 'expired') && (
            <div className="py-6 text-center">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {phase === 'expired' ? 'This link has expired' : 'This link is no longer valid'}
              </h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                No problem — just reply to our text message and we'll send you a fresh link, or we'll give you a call.
              </p>
            </div>
          )}

          {phase === 'error' && (
            <div className="py-6 text-center">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Booking is temporarily unavailable</h2>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Please try again in a few minutes.</p>
              <button
                onClick={load}
                className="mt-4 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Try again
              </button>
            </div>
          )}

          {phase === 'booked' && data.booked && (
            <div className="py-6 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-2xl dark:bg-green-900/30">✓</div>
              <h2 className="mt-3 text-base font-semibold text-gray-900 dark:text-gray-100">You're booked!</h2>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                {greeting} We'll call you on
              </p>
              <p className="mt-1 text-base font-semibold text-primary-700 dark:text-primary-400">{data.booked.label}</p>
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                We'll text you a reminder before the call. Need a different time? Just reply to our text.
              </p>
            </div>
          )}

          {phase === 'pick' && (
            <>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{greeting}</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Pick a time that works for a quick call about the study you're interested in — usually 10&ndash;15 minutes.
              </p>

              {notice && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  {notice}
                </div>
              )}

              {(!data.days || data.days.length === 0) ? (
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                  No open times in the next few days — we'll reach out to find a time that works.
                </p>
              ) : (
                <>
                  <div className="mt-4 flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Pick a day">
                    {data.days.map((d, i) => (
                      <button
                        key={d.date}
                        role="tab"
                        aria-selected={i === dayIdx}
                        onClick={() => setDayIdx(i)}
                        className={`flex-shrink-0 rounded-full px-3.5 py-2 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                          i === dayIdx
                            ? 'bg-primary-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>

                  <div className="mt-3 grid grid-cols-3 gap-2">
                    {(day?.slots || []).map((s) => (
                      <button
                        key={s.iso}
                        onClick={() => book(s)}
                        disabled={!!submitting}
                        className="rounded-lg border border-primary-600 px-2 py-2.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20"
                      >
                        {submitting === s.iso ? '…' : s.label}
                      </button>
                    ))}
                  </div>
                  {data.timezone && (
                    <p className="mt-3 text-center text-xs text-gray-400">
                      Times shown in {data.timezone.replace(/_/g, ' ').replace('America/', '')} time
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-gray-400">
          Questions? Reply to our text message any time. Msg &amp; data rates may apply.
        </p>
      </div>
    </div>
  );
}
